/**
 * Pure helpers behind the region emissions map.
 *
 * Everything here is deliberately outside the ECharts effect so it can be tested
 * without a DOM or a chart instance. The component is a thin shell over it.
 */

export interface RegionCell {
  h3: string;
  /**
   * Ring of [lat, lng] pairs describing the region's shape.
   *
   * Load-bearing again since the switch from hexagons to real administrative
   * regions: a NUTS region's outline cannot be derived from its id the way a
   * hexagon's could, so the server sends it.
   */
  boundary?: [number, number][];
  label: string | null;
  emissionWtwKg: number;
  intensity: number | null;
  loadPerfTonKm: number;
  shipmentCount: number;
  /** Places inside this region that shipments were picked up from, busiest first. */
  towns?: RegionTown[];
  /** How many there are in total, so a truncated list can say "and N more". */
  townCount?: number;
}

/** A movement between two regions, drawn as an arc. */
export interface RegionFlow {
  originH3: string;
  destH3: string;
  /** [lat, lng] centres, so no H3 maths is needed client-side. */
  origin: [number, number];
  dest: [number, number];
  originLabel: string | null;
  destLabel: string | null;
  emissionWtwKg: number;
  intensity: number | null;
  shipmentCount: number;
}

export interface FlowCoverage {
  shown: number;
  total: number;
  emissionsShownKg: number;
  emissionsTotalKg: number;
}

export interface RegionResponse {
  resolution: 2 | 3 | 4;
  ignoredFilters: string[];
  cells: RegionCell[];
  flows: RegionFlow[];
  flowCoverage: FlowCoverage;
  totals: { emissionWtwKg: number; loadPerfTonKm: number; shipmentCount: number };
  coverage: {
    shipmentsWithCpi: number;
    shipmentsTotal: number;
    excludedNoGeo: number;
    /** Shipments whose pickup fell outside every known administrative region. */
    excludedNoRegion?: number;
    excludedNoRegionKg?: number;
  };
  lastRefreshedAt: string | null;
}

/** What the component should render, decided from the response alone. */
export type RegionMapState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "empty" }
  /** Everything sits in one cell: a choropleth of one colour says nothing. */
  | { kind: "single"; cell: RegionCell }
  | { kind: "map"; cells: RegionCell[] };

/**
 * Shape check for whatever the network actually handed back.
 *
 * `useRegionEmissions` types the axios response as `RegionResponse` but that is
 * only a cast — nothing on the wire enforces it. A test's catch-all mock (or a
 * misbehaving proxy, or a future endpoint change) can hand this component an
 * object of a completely different shape, e.g. the generic analytics envelope
 * `{ data: [] }` used elsewhere on this page. Every reader downstream assumes
 * `cells`/`flows` are arrays and `coverage`/`totals` are objects, so that has to
 * be checked once, here, rather than re-guarded at every call site.
 */
export function isRegionResponse(data: unknown): data is RegionResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    Array.isArray(d.cells) &&
    Array.isArray(d.flows) &&
    typeof d.coverage === "object" &&
    d.coverage !== null &&
    typeof d.totals === "object" &&
    d.totals !== null
  );
}

export function resolveState(
  data: RegionResponse | null,
  isLoading: boolean,
  error?: Error | null
): RegionMapState {
  if (isLoading) return { kind: "loading" };
  if (error) return { kind: "error" };
  if (!data || !isRegionResponse(data) || data.cells.length === 0) return { kind: "empty" };
  if (data.cells.length === 1) return { kind: "single", cell: data.cells[0] };
  return { kind: "map", cells: data.cells };
}

/**
 * Emission range for the colour ramp.
 *
 * Anchored at 0 rather than the smallest cell's value. Starting the ramp at the
 * minimum makes the least-polluting region the full "low" colour, which reads as
 * "clean" when it may be substantial in absolute terms.
 */
export function emissionRange(cells: RegionCell[]): { min: number; max: number } {
  if (cells.length === 0) return { min: 0, max: 0 };
  return {
    min: 0,
    max: cells.reduce((hi, c) => (c.emissionWtwKg > hi ? c.emissionWtwKg : hi), 0),
  };
}

/** kg with a thousands separator, or tonnes once it stops being readable. */
export function formatEmissions(kg: number): string {
  if (kg >= 1_000_000) return `${(kg / 1_000_000).toFixed(1)}M kg`;
  if (kg >= 10_000) return `${Math.round(kg / 1000).toLocaleString()} t`;
  return `${Math.round(kg).toLocaleString()} kg`;
}

/** Intensity is kg CO2e per ton-km; three decimals is the useful precision. */
export function formatIntensity(intensity: number | null): string | null {
  if (intensity === null) return null;
  return intensity.toFixed(3);
}

/**
 * Share of a tenant's shipments the map cannot place, as a percentage.
 *
 * Returned so the caption can be suppressed when it rounds to nothing — a
 * "0.0% excluded" note is noise, but hiding a real 12% would be dishonest.
 */
export function excludedShare(coverage: RegionResponse["coverage"]): number {
  if (coverage.shipmentsTotal === 0) return 0;
  return (coverage.excludedNoGeo / coverage.shipmentsTotal) * 100;
}

export function shouldShowExcludedNotice(coverage: RegionResponse["coverage"]): boolean {
  return coverage.excludedNoGeo > 0 && excludedShare(coverage) >= 0.05;
}

/**
 * Whether to warn that the map is less filtered than the charts beside it.
 *
 * The region summary's grain cannot honour fuelType, emissionSource or shipperId.
 * The endpoint names them; the UI must show them. A map quietly ignoring a filter
 * its neighbours applied is how a dashboard comes to contradict itself.
 */
export function shouldWarnIgnoredFilters(data: RegionResponse | null): boolean {
  return (data?.ignoredFilters?.length ?? 0) > 0;
}

/**
 * Sequential ramp, light to dark. Single hue: the fill encodes ONE quantity, and a
 * multi-hue ramp would invite reading it as a second. Avoids red/green entirely so
 * it survives the common colour-vision deficiencies.
 */
export const RANK_RAMP = [
  "#eaf0f8",
  "#cfdff1",
  "#a9c7e6",
  "#7fa9d8",
  "#5488c6",
  "#2f68ad",
  "#1b4a8a",
] as const;

export type Rgb = [number, number, number];

/** deck.gl takes colours as [r,g,b] arrays, not CSS strings. */
export function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export const RANK_RAMP_RGB: Rgb[] = RANK_RAMP.map(hexToRgb);

/**
 * Colour each cell by its RANK among the cells shown, not by its magnitude.
 *
 * Why rank. Real tenants are heavily skewed — one cell held 77% of a month's
 * emissions in the data this was built against — so a linear ramp paints one cell
 * dark and leaves every other indistinguishable, hiding the entire tail.
 *
 * The cost is that shade no longer means "this much CO2e". The legend MUST therefore
 * be labelled as a ranking and must not show values, or the map claims a precision
 * it does not have. Absolute numbers stay available in the tooltip.
 *
 * Ties share a colour: equal emissions must not be drawn as different.
 */
export function rankColors(cells: RegionCell[]): Map<string, Rgb> {
  const colors = new Map<string, Rgb>();
  if (cells.length === 0) return colors;

  const sorted = [...cells].sort((a, b) => a.emissionWtwKg - b.emissionWtwKg);

  let distinctSeen = -1;
  let previous: number | null = null;
  const rankOf = new Map<string, number>();
  for (const cell of sorted) {
    if (previous === null || cell.emissionWtwKg !== previous) {
      distinctSeen += 1;
      previous = cell.emissionWtwKg;
    }
    rankOf.set(cell.h3, distinctSeen);
  }

  const distinctCount = distinctSeen + 1;
  for (const cell of cells) {
    const rank = rankOf.get(cell.h3) ?? 0;
    // One distinct value means one colour; use the darkest so a single-value map
    // does not render as "almost nothing".
    const idx =
      distinctCount === 1
        ? RANK_RAMP.length - 1
        : Math.round((rank / (distinctCount - 1)) * (RANK_RAMP.length - 1));
    colors.set(cell.h3, RANK_RAMP_RGB[idx]);
  }
  return colors;
}

/**
 * Share of the selection's total emissions held by one cell, as a percentage.
 *
 * Shown ON the hexagons so the map answers "how much" without a hover. Rank shading
 * deliberately does not encode magnitude, so without this number the map can say
 * which region is biggest but never by how much.
 */
export function cellShare(cell: RegionCell, totalKg: number): number {
  if (totalKg <= 0) return 0;
  return (cell.emissionWtwKg / totalKg) * 100;
}

/**
 * The text drawn on a hexagon: its name and its share.
 *
 * Returns null when a cell is too small to label — below ~2% the text is wider than
 * the hexagon and overlaps its neighbours, which costs more legibility than the
 * label adds. Those cells keep their tooltip.
 */
export function cellLabelText(
  cell: RegionCell,
  totalKg: number,
  unnamedFallback: string,
  minSharePct = 2
): string | null {
  const share = cellShare(cell, totalKg);
  if (share < minSharePct) return null;
  const name = cell.label ?? unnamedFallback;

  // BOTH figures, on every label. Showing one or the other depending on size put
  // "78%" and "12 t" side by side in the same view — two units a reader cannot
  // compare at a glance. Both together is consistent and answers both questions:
  // how large a slice of the footprint, and how much CO2e in absolute terms.
  return `${name}\n${formatShare(share)} | ${formatTonnes(cell.emissionWtwKg)}`;
}

/**
 * Emissions in TONNES, always, for on-map labels.
 *
 * formatEmissions switches between kilograms and tonnes at a threshold, which is
 * right in a tooltip read one at a time but wrong on a map: it put "8,234 kg" beside
 * "13 t" in the same view, so a reader had to convert before comparing two regions.
 * Precision varies here; the unit never does.
 */
export function formatTonnes(kg: number): string {
  const t = kg / 1000;
  if (t >= 100) return `${Math.round(t).toLocaleString()} t`;
  if (t >= 10) return `${t.toFixed(0)} t`;
  if (t >= 1) return `${t.toFixed(1)} t`;
  if (t > 0) return `${t.toFixed(2)} t`;
  return "0 t";
}

/**
 * A region's share, always as a percentage.
 *
 * ONE unit across the whole map, deliberately. An earlier version switched to
 * absolute tonnes below 1% to avoid labels reading "0%", which produced "78%" and
 * "12 t" side by side in the same view — two units a reader cannot compare at a
 * glance. Precision varies instead of the unit: a decimal place below 10%, and an
 * explicit "<0.1%" rather than a misleading zero.
 *
 * Absolute kilograms and tonnes still exist, in the tooltip, where there is room to
 * label them.
 */
export function formatShare(share: number): string {
  if (share >= 10) return `${share.toFixed(0)}%`;
  if (share >= 0.1) return `${share.toFixed(1)}%`;
  if (share > 0) return "<0.1%";
  return "0%";
}

/**
 * Text colour that stays legible on whatever fill sits behind it.
 *
 * The rank ramp runs from near-white to deep navy, so a single text colour is
 * unreadable at one end or the other — dark text vanishes on the darkest cells,
 * which are exactly the ones a reader most needs to identify. Chooses by relative
 * luminance (ITU-R BT.601 weighting, which tracks perceived brightness better than
 * a plain mean of the channels).
 */
export function textColorOn(fill: Rgb): Rgb {
  const [r, g, b] = fill;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? [22, 32, 45] : [255, 255, 255];
}

/** Halo that contrasts with the text, so the pairing works on either fill. */
export function haloColorOn(fill: Rgb): [number, number, number, number] {
  const [r, g, b] = textColorOn(fill);
  return r > 128 ? [20, 30, 42, 210] : [255, 255, 255, 220];
}

/** Most labels a single map should carry before it reads as clutter. */
export const MAX_MAP_LABELS = 6;

/**
 * Which cells get a label drawn on them.
 *
 * deck.gl's CollisionFilterExtension was tried here first and hid EVERY label
 * rather than only the colliding ones, so selection is done explicitly instead:
 * biggest emitters first, capped, and only above the share threshold. Deterministic,
 * testable, and it degrades predictably — an unlabelled cell still has its tooltip.
 */
export function selectLabelledCells(
  cells: RegionCell[],
  totalKg: number,
  unnamedFallback: string,
  limit: number = MAX_MAP_LABELS,
  minSharePct = 2
): RegionCell[] {
  return [...cells]
    .filter((c) => cellLabelText(c, totalKg, unnamedFallback, minSharePct) !== null)
    .sort((a, b) => b.emissionWtwKg - a.emissionWtwKg)
    .slice(0, limit);
}

/**
 * Whether to tell the user the arcs are incomplete.
 *
 * Arcs overplot where shaded regions do not, so the map draws only the heaviest.
 * For a concentrated tenant that is nearly everything; for a diffuse one 60 arcs can
 * be a third of the footprint. Saying nothing in the second case is the failure mode
 * the whole flow layer was cancelled over once already.
 */
export function shouldWarnFlowCoverage(coverage: FlowCoverage | undefined): boolean {
  if (!coverage || coverage.total === 0) return false;
  return coverage.shown < coverage.total;
}

/** Percentage of flow emissions the drawn arcs represent. */
export function flowSharePct(coverage: FlowCoverage | undefined): number {
  if (!coverage || coverage.emissionsTotalKg <= 0) return 0;
  return (coverage.emissionsShownKg / coverage.emissionsTotalKg) * 100;
}

/** Arc width by emissions — sqrt so perceived AREA tracks the value, not width. */
export function arcWidth(flow: RegionFlow, maxKg: number): number {
  if (maxKg <= 0) return 1;
  return 1 + Math.sqrt(flow.emissionWtwKg / maxKg) * 7;
}

/** One end of a route, ready to draw as a marker. `[lng, lat]` for deck.gl. */
export interface FlowEndpoint {
  position: [number, number];
  label: string | null;
}

/**
 * Route ends, split into the two marker sets the map draws.
 *
 * Direction was carried only by the arc's pale-to-saturated gradient, which is
 * invisible to a reader who cannot separate the two hues — and the strapline
 * that used to explain it in words has been removed. A filled dot at departure
 * and an open ring at arrival encode it as SHAPE instead, which survives both
 * greyscale and the gradient being washed out by the basemap underneath.
 *
 * Deduplicated by position: a region is a hub, so one distribution centre is the
 * origin of many routes. Stacking forty identical markers on it wastes draw
 * calls and, because the fills are semi-transparent, darkens that one spot
 * against every other until it reads as a different colour entirely.
 *
 * A place that both sends and receives keeps a marker in BOTH sets — collapsing
 * those would hide that it is a hub rather than a terminus.
 */
export function flowEndpoints(flows: RegionFlow[]): {
  departures: FlowEndpoint[];
  arrivals: FlowEndpoint[];
} {
  // keyed by position, so the first label for a place wins and order is kept
  const departures = new Map<string, FlowEndpoint>();
  const arrivals = new Map<string, FlowEndpoint>();

  for (const flow of flows) {
    // [lat, lng] on the wire, [lng, lat] for deck.gl.
    const from: [number, number] = [flow.origin[1], flow.origin[0]];
    const to: [number, number] = [flow.dest[1], flow.dest[0]];
    const fromKey = `${from[0]},${from[1]}`;
    const toKey = `${to[0]},${to[1]}`;

    if (!departures.has(fromKey)) {
      departures.set(fromKey, { position: from, label: flow.originLabel });
    }
    if (!arrivals.has(toKey)) {
      arrivals.set(toKey, { position: to, label: flow.destLabel });
    }
  }

  return {
    // Array.from, not spread: this project's TS target predates iterable spread.
    departures: Array.from(departures.values()),
    arrivals: Array.from(arrivals.values()),
  };
}

/**
 * Esri's World Light Gray Canvas.
 *
 * A pale, low-contrast basemap chosen because it was designed to sit UNDER a
 * choropleth: the data layer has to stay the loudest thing on the map, and a
 * full-colour street map competes with the rank ramp for the reader's attention.
 *
 * CARTO Positron was the first choice and had to be abandoned — its anonymous
 * endpoint now answers every request with an "API KEY REQUIRED" watermark tile at
 * HTTP 200, so it fails without failing. This is a PROTOTYPE source: it is keyless,
 * which is what makes it usable for a design review, but the production vendor is
 * still an open decision. Changing it is changing this constant.
 *
 * Esri's REST tiles are addressed row-then-column, so the path reads {z}/{y}/{x}
 * rather than the {z}/{x}/{y} every other provider uses. Getting that backwards
 * yields a map that loads real tiles of entirely the wrong places.
 *
 * Attribution is NOT optional: Esri's terms and OpenStreetMap's licence both
 * require it, so the component renders it whenever these tiles are on.
 */
export const BASEMAP_TILE_URLS = [
  "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
];

/**
 * The same basemap's place names, published as a separate transparent layer.
 *
 * This exists because a translucent fill was never going to solve the real
 * problem. Lowering alpha far enough to read a city name through a region also
 * sinks the pale end of the rank ramp into the grey land — the map loses its
 * bottom half to save its background.
 *
 * Drawing the names ABOVE the data instead removes the trade-off: they are never
 * occluded at any opacity, so the fill only has to let coastline and water hint
 * through rather than carry text. It is the standard choropleth sandwich —
 * ground, data, labels — and it is why web maps put street names on top of their
 * traffic overlays rather than under them.
 */
export const BASEMAP_LABEL_TILE_URLS = [
  "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
];

/**
 * How a region's fill combines with whatever is beneath it.
 *
 * With no basemap this is ordinary painting — there is nothing underneath worth
 * preserving. With one, it MULTIPLIES: the result is basemap x colour, so the
 * region reads as tinted and every road, river, polder and boundary inside it
 * survives at full sharpness.
 *
 * This replaced a zoom-dependent fill opacity, which could never have worked.
 * Normal blending REPLACES what it covers; transparency only decides how much is
 * lost, so there was no value that both let the basemap through and kept the pale
 * end of the rank ramp distinguishable from grey land. Multiplying removes the
 * trade-off instead of balancing it — which is also why the fill is fully opaque
 * now: with these factors the source alpha never reaches the colour channels, so
 * an alpha dial would be inert.
 *
 * Pairs with deckClearColor: multiplying against a transparent-black canvas, which
 * is what shows while tiles are still loading, renders every region solid black.
 */
export function regionBlendParameters(
  showBasemap: boolean
): Record<string, unknown> {
  if (!showBasemap) return {};
  return {
    blend: true,
    blendColorOperation: "add",
    blendColorSrcFactor: "dst",
    blendColorDstFactor: "zero",
    blendAlphaOperation: "add",
    blendAlphaSrcFactor: "one",
    blendAlphaDstFactor: "one-minus-src-alpha",
  };
}

/**
 * An opaque white sheet under the tiles, drawn only while the basemap is on.
 *
 * The multiply above operates on the FRAMEBUFFER, not on the page behind the
 * canvas — so a CSS background cannot stand in for it. Without something opaque
 * already painted, a region multiplied against the cleared canvas comes out solid
 * black, which is exactly what shows in the moment before the first tile lands.
 *
 * deck.gl 9 does not accept clearColor through its typed `parameters`, so the
 * ground is a layer rather than a render setting.
 */
export const GROUND_POLYGON: [number, number][] = [
  [-180, -85.05],
  [180, -85.05],
  [180, 85.05],
  [-180, 85.05],
];

export function shouldDrawGround(showBasemap: boolean): boolean {
  return showBasemap;
}

/**
 * Tiles are 256px with no @2x variant, so on a retina screen deck stretches each
 * one over twice as many device pixels and every place name comes out soft.
 * Fetching one zoom level deeper is four times as many tiles, each drawn at half
 * size — native resolution at devicePixelRatio 2.
 */
export const BASEMAP_ZOOM_OFFSET = 1;

/**
 * The country outline we ship ourselves is a STAND-IN for a basemap, so the two
 * must never draw together: doubled coastlines at slightly different
 * generalisations read as a rendering fault. It also keeps the chart usable with
 * no network, which the tiles cannot.
 */
export function shouldDrawOwnOutline(showBasemap: boolean): boolean {
  return !showBasemap;
}

/**
 * How far the camera may travel.
 *
 * The ceiling used to be 9, on the reasoning that NUTS-3 is the finest grain the
 * data has and there was nothing further to reveal. That held only while the map
 * drew regions on an empty plate. With a street map underneath there IS more to
 * reveal past 9 — which town a region's emissions sit in, which motorway corridor
 * a lane follows — even though the shading itself stops subdividing. 13 is where a
 * basemap starts drawing individual streets.
 *
 * The floor stays at 3: below it a tenant's footprint is a speck on a continent.
 */
export const MIN_ZOOM = 3;
export const MAX_ZOOM = 13;

/**
 * Zoom that fits a bounding box to a viewport, solved rather than guessed.
 *
 * Web Mercator puts 360 degrees of longitude across 512 * 2^zoom pixels and
 * compresses a degree of latitude by cos(phi). Fitting both axes and taking the
 * tighter guarantees the whole footprint is on screen.
 *
 * This replaced a fixed `log2(360 / span) - 1.2` offset, which cannot be right for
 * two different aspect ratios: on a 1114x660 viewport it framed a 5.8-degree
 * footprint inside 29 degrees, opening the map five times too far out.
 */
export function fitZoom(
  spanLng: number,
  spanLat: number,
  centreLat: number,
  width: number,
  height: number
): number {
  // Floors stop a single-region selection dividing by zero into infinite zoom.
  const lng = Math.max(spanLng, 0.05);
  const lat = Math.max(spanLat, 0.05);
  const w = Math.max(width || 0, 320);
  const h = Math.max(height || 0, 240);
  const zLng = Math.log2((360 * w) / (512 * lng));
  const zLat = Math.log2((360 * h * Math.cos((centreLat * Math.PI) / 180)) / (512 * lat));
  // A little air, so the outermost region is not flush against the frame.
  const zoom = Math.min(zLng, zLat) - 0.28;
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
}

/**
 * How far the region fill has receded, 0 (full tint) to 1 (gone).
 *
 * Zoom past about 10 and a single region fills the viewport. The fill encodes
 * RANK AMONG THE REGIONS ON SCREEN, so with only one of them there is nothing
 * left for it to say — while it still tints every street name underneath into
 * illegibility. The overlay is costing the reader something and returning
 * nothing.
 *
 * So it fades out across exactly the range where that stops being true: full
 * strength while regions can still be compared, gone by the time the basemap is
 * drawing individual streets. The region keeps its outline and its own label, so
 * you never lose track of which one you are inside.
 */
export const FADE_START_ZOOM = 9.5;
export const FADE_END_ZOOM = 12;

export function basemapFade(zoom: number): number {
  if (!Number.isFinite(zoom)) return 0;
  const t = (zoom - FADE_START_ZOOM) / (FADE_END_ZOOM - FADE_START_ZOOM);
  return Math.min(Math.max(t, 0), 1);
}

/**
 * Fade toward white, which under a multiply blend IS fading toward invisible:
 * multiplying by white leaves the basemap exactly as it was. Lerping the colour
 * rather than the alpha is what makes this work at all — with these blend factors
 * the source alpha never reaches the colour channels.
 */
export function fadedFill(fill: Rgb, fade: number): Rgb {
  const t = Math.min(Math.max(fade, 0), 1);
  return [
    Math.round(fill[0] + (255 - fill[0]) * t),
    Math.round(fill[1] + (255 - fill[1]) * t),
    Math.round(fill[2] + (255 - fill[2]) * t),
  ];
}

/**
 * The region outline has to change colour with the basemap.
 *
 * White works on a bare plate and is invisible under a multiply blend — white is
 * multiply's identity. Once the fill fades out at deep zoom the outline is the
 * ONLY thing still showing where a region begins and ends, so it cannot be the
 * one colour the blend cannot draw.
 */
export function cellLineColor(showBasemap: boolean): [number, number, number, number] {
  return showBasemap ? [96, 116, 146, 255] : [255, 255, 255, 200];
}

/** A place inside a region that shipments were picked up from. */
export interface RegionTown {
  name: string;
  shipmentCount: number;
}

/**
 * The pickup towns line for a region's tooltip.
 *
 * Replaces the shipment count, which said how MUCH came from here and never where
 * — and the region's own name was already in the tooltip's title, so the count was
 * the only line not telling the reader something new.
 *
 * Truncates with an explicit remainder. A province holds hundreds of towns, and a
 * list that simply stops implies it is complete; "and 12 more" is the difference
 * between a summary and a wrong answer.
 */
export function townsLine(
  towns: RegionTown[] | undefined,
  total: number | undefined,
  more: (n: number) => string
): string | null {
  if (!towns?.length) return null;
  const names = towns.map((t) => t.name);
  const remainder = Math.max((total ?? names.length) - names.length, 0);
  return remainder > 0 ? `${names.join(", ")} ${more(remainder)}` : names.join(", ");
}


/**
 * Share of a tenant's shipments that fall outside any known administrative region.
 *
 * These are no longer drawn — an H3 fallback hexagon overlaps the regions around it
 * rather than tiling with them, which blurs the very boundaries the map exists to
 * show. Hiding them is only defensible if the map says so, and the number is not
 * uniformly small: measured at 0.01% for the tenant this was built against, but
 * NUTS coverage runs 91.1%-99.6% across production tenants, so for a UK-heavy one
 * this is closer to one shipment in ten.
 */
export function excludedNoRegionShare(coverage: RegionResponse["coverage"]): number {
  if (!coverage.shipmentsTotal) return 0;
  return ((coverage.excludedNoRegion ?? 0) / coverage.shipmentsTotal) * 100;
}

/**
 * Same 0.05% floor as the no-geo notice. Below it the line rounds to "0.0%" and is
 * pure noise; at or above it, staying silent would misrepresent the map.
 */
export function shouldShowNoRegionNotice(coverage: RegionResponse["coverage"]): boolean {
  return (coverage.excludedNoRegion ?? 0) > 0 && excludedNoRegionShare(coverage) >= 0.05;
}
